/** @file clip.mirrorHalf: bakes the mirrored half of a loop into keys and clears the `mirrorHalf` flag. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Clip, ClipKey } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import { mirrorPose } from '../pose/mirrorPose';
import { checkClip } from '../puppetDoc/checkClip';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { keyAt } from './keyAt';

/** `key` mirrored onto frame `frame`: nodes through `mirrorPose`, events onto twins, ease and hold kept. */
function mirrorKey(p: Puppet, key: ClipKey, frame: number, warnings: Warning[]): ClipKey {
  const m = mirrorPose(p, key.nodes);
  warnings.push(...m.warnings);
  const events = key.events?.map((e) => compact({
    ...e, node: e.node === undefined ? undefined : (p.nodes[e.node]?.mirror ?? e.node),
    pos: e.pos === undefined ? undefined : [-e.pos[0] + 0, e.pos[1], e.pos[2]] as [number, number, number],
  }));
  return compact({ frame, ease: key.ease, hold: key.hold, nodes: m.pose, events });
}

function checkLoop(clip: Clip): number {
  const len = clip.frames - clip.loopStart;
  if (len % 2 !== 0) throwError(ERROR_CODES.VALIDATION, { length: len }, { path: '/input/clip', hint: 'frames − loopStart must be even to mirror half' });
  if (keyAt(clip, clip.loopStart) === undefined) {
    throwError(ERROR_CODES.VALIDATION, { loopStart: clip.loopStart }, { path: '/input/clip', hint: 'key the loop start first (design 15.6)' });
  }
  return len / 2;
}

/**
 * `clip.mirrorHalf { clip, from? }` → `{ half }`: with `first` (default) the keys of the second half go and each key
 * of [loopStart, loopStart + half) is mirrored to frame + half; `second` mirrors the second half back onto the first.
 * Sets `mirrorHalf: false`. Inverse C.
 */
export const clipMirrorHalfCommand: CommandDef<'clip.mirrorHalf'> = {
  name: 'clip.mirrorHalf',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'from']);
    return compact({ clip: r.str('clip'), from: r.opt('from', (k) => r.oneOf(k, ['first', 'second'] as const)) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name, clip } = findClip(draft, input.clip);
    const half = checkLoop(clip);
    const inverse = [clipInverse(clip)];
    const mid = clip.loopStart + half;
    const inFirst = (f: number): boolean => f >= clip.loopStart && f < mid;
    const source = input.from === 'second' ? clip.keys.filter((k) => k.frame >= mid && k.frame < clip.frames) : clip.keys.filter((k) => inFirst(k.frame));
    const kept = clip.keys.filter((k) => (input.from === 'second' ? !inFirst(k.frame) : k.frame < mid));
    const warnings: Warning[] = [];
    const baked = source.map((k) => mirrorKey(draft.puppet, k, input.from === 'second' ? k.frame - half : k.frame + half, warnings));
    clip.keys = [...kept, ...baked].sort((a, b) => a.frame - b.frame);
    clip.mirrorHalf = false;
    checkClip(name, clip);
    return { result: { half }, warnings, inverse };
  },
};
